import {
  ErrorHandler,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideMssFormly } from '@shared/components/formly/mss-formly';
import { AuthorizeService } from '@shared/services/authorize.service';
import { ConfigService } from '@shared/services/config.service';
import { MaintenanceService } from '@shared/services/maintenance.service';
import { ThemeService } from '@shared/services/theme.service';
import { SharedModule } from '@shared/shared.module';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { provideChartTheme } from './app/services/chart-theme';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
  Sentry.init({
    dsn: environment.sentry.dsn,
    attachStacktrace: true,
    debug: false,
    environment: 'production',
    maxValueLength: 5000,
    // // Set tracesSampleRate to 1.0 to capture 100%
    // // of transactions for performance monitoring.
    // // We recommend adjusting this value in production
    tracesSampleRate: 0.2,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(BrowserModule, FormsModule, ReactiveFormsModule, SharedModule.forRoot(environment.sharedConfig)),
    provideMssFormly(),
    provideChartTheme(),
    provideAppInitializer(() => {
      const configService = inject(ConfigService);
      const authorizeService = inject(AuthorizeService);
      const maintenanceService = inject(MaintenanceService);
      const themeService = inject(ThemeService);

      // If an API is in maintenance mode, still boot the app so the maintenance page can be shown
      return configService
        .load()
        .then(() => themeService.setDarkModeAllowed(configService.allowDarkMode))
        .then(() => authorizeService.initialize())
        .catch((error: unknown) => maintenanceService.ignoreErrorIfInMaintenance(error));
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
  ],
})
  // eslint-disable-next-line no-console
  .catch(err => console.error(err));
